import { AnimatePresence } from 'framer-motion';
import React, { FC, useMemo, useRef } from 'react';
import { Arrow, mergeRefs, Placement, useHover, useLayer } from 'react-laag';
import { useTheme } from 'styled-components';

import { StyledTooltip } from './styled';

export type TooltipVariant = 'light' | 'dark';

export interface TooltipProps {
  children: JSX.Element | JSX.Element[] | string | number;
  text: JSX.Element | JSX.Element[] | string;
  variant?: TooltipVariant;
  background?: string;
  border?: string;
  isVisible?: boolean;
  position?: Placement;
  tooltipStyle?: { [key: string]: string | number };
  offset?: number;
  opacity?: string;
  zoom?: number;
  visibleAfterWidth?: number;
  visibleAfterHeight?: number;
  auto?: boolean;
  isHover?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  maxWidth?: number;
  noArrow?: boolean;
  delayEnter?: number;
  delayLeave?: number;
  closeTooltip?: () => void;
  adjustToolTipPosition?: { [key: string]: string | number };
}

const Tooltip: FC<TooltipProps> = ({
  children,
  text,
  variant = 'light',
  background,
  border,
  isVisible,
  position,
  tooltipStyle = {},
  offset = 12,
  zoom = 1,
  visibleAfterWidth,
  visibleAfterHeight,
  auto = true,
  isHover = true,
  disabled = false,
  multiline = false,
  maxWidth,
  noArrow,
  delayEnter = 50,
  delayLeave = 50,
  closeTooltip = () => {},
  adjustToolTipPosition,
}) => {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>();

  const [isOver, hoverProps] = useHover({ delayEnter, delayLeave });

  const isTooltipVisible = useMemo(() => {
    if (!visibleAfterWidth && !visibleAfterHeight) {
      return true;
    }

    let visible = false;

    if (ref?.current?.getBoundingClientRect) {
      if (
        (visibleAfterWidth &&
          ref.current.getBoundingClientRect().width >= visibleAfterWidth) ||
        (visibleAfterHeight &&
          ref.current.getBoundingClientRect().height >= visibleAfterHeight)
      ) {
        visible = true;
      }
    }

    return visible;
  }, [visibleAfterWidth, visibleAfterHeight, zoom, ref?.current]);

  const isOpen = useMemo(() => {
    if (disabled) return false;

    if (isHover) {
      return isOver && isTooltipVisible;
    }

    return isVisible;
  }, [disabled, isHover, isVisible, isOver, isTooltipVisible]);

  const { triggerProps, layerProps, arrowProps, renderLayer } = useLayer({
    isOpen,
    placement: position,
    triggerOffset: offset,
    auto,
    possiblePlacements: [
      'top-center',
      'right-center',
      'left-center',
      'bottom-center',
    ],
    onOutsideClick: closeTooltip,
  });

  const arrowColor =
    background ||
    (variant === 'dark' ? theme.color.pickledBluewood : theme.color.white);

  const resolvedMultiline =
    multiline ||
    Boolean(maxWidth) ||
    tooltipStyle.whiteSpace === 'pre-wrap' ||
    Boolean(tooltipStyle.maxWidth);

  const trigger = useMemo(() => {
    if (['string', 'number'].includes(typeof children)) {
      return (
        <span
          {...triggerProps}
          {...hoverProps}
          ref={mergeRefs(triggerProps.ref, ref)}
        >
          {children}
        </span>
      );
    }

    return React.cloneElement(children as JSX.Element, {
      ...triggerProps,
      ...hoverProps,
      ref: mergeRefs(triggerProps.ref, ref),
    });
  }, [children, triggerProps, hoverProps]);

  return (
    <>
      {trigger}
      {renderLayer(
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment*/
        /* @ts-ignore*/
        <AnimatePresence>
          {isOpen && (
            <StyledTooltip
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{
                opacity: parseFloat('1'),
                scale: 1,
                ...adjustToolTipPosition,
              }}
              exit={{ opacity: 0, scale: 0.9 }}
              $variant={variant}
              $multiline={resolvedMultiline}
              $maxWidth={maxWidth}
              background={background}
              border={border}
              zoom={zoom}
              {...layerProps}
              style={{
                ...tooltipStyle,
                ...layerProps.style,
              }}
            >
              {text}
              {!noArrow && (
                <Arrow
                  {...arrowProps}
                  backgroundColor={arrowColor}
                  borderColor={arrowColor}
                  size={6 * zoom}
                  roundness={0.5}
                />
              )}
            </StyledTooltip>
          )}
        </AnimatePresence>,
      )}
    </>
  );
};

export default Tooltip;
